import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect } from "react";
import Loader from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const ConferenceRegistrationForm = () => {
  const queryClient = useQueryClient();
  const [isProfileUpdated, setIsProfileUpdated] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL
  // Fetch user data
  const {
    data: authUser,
    isFetching: isUserLoading,
    isError: isUserError,
  } = useQuery({
    queryKey: ["authUser"],
    staleTime: Infinity,
    cacheTime: 10 * 60 * 1000,
  });

  // Set form data once authUser data is fetched
  const [formData, setFormData] = useState({
    prefix: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    designation: "",
    institute: "",
    gender: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    country: "",
    phoneNumber: "",
    conferenceCategory: "",
    membershipNumber: "",
    registrationCategory: "",
    amount: "",
  });

  // Mutation for conference registration
  const {
    mutate: registerConferenceMutation,
    isPending: isRegistering,
    isError: formError,
    error,
  } = useMutation({
    mutationFn: async (formData) => {
      const res = await fetch(`${API_URL}/registration/update-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ formData }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Error submitting form data!");
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authUser"] });
      toast.success("Profile updated successfully")
      setIsProfileUpdated(true);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Handle form data changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    registerConferenceMutation(formData);
  };

  // Handle side-effects: Update formData when authUser data is available
  useEffect(() => {
    if (authUser) {
      setFormData((prevData) => ({
        ...prevData,
        firstName: authUser?.firstName || "",
        middleName: authUser?.middleName || "",
        lastName: authUser?.lastName || "",
        email: authUser?.email || "",
      }));
    }
  }, [authUser]);

  // Show loading state while fetching user data
  if (isUserLoading) {
    return (
      <div className="h-[400px] flex justify-center items-center">
        <Loader sx={() => ({ color: "#4f46e5" })} />
      </div>
    );
  }

  // Show an error message if there's an error fetching user data
  if (isUserError) {
    return (
      <div className="p-6 text-center text-red-500">
        Error fetching user data.
      </div>
    );
  }

  // Show success message if profile is updated
  if (authUser?.profileUpdated && authUser?.registrationStatus === "Not registered") {
    return (
      <div className="p-6 text-center text-green-600 bg-green-100 rounded-md">
        Profile updated successfully. You can now proceed to the fee payment.
        <button
          onClick={() => navigate("/conference-registration/info")}
          className="ml-4 mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Proceed to Fee Payment
        </button>
      </div>
    );
  }
  if(authUser?.profileUpdated && authUser?.registrationStatus === "Under verification") {
    return (
      <div className="p-6 text-center text-green-600 bg-green-100 rounded-md">
        You have completed all the steps for the registration and your payment is under verification.
      </div>
    );
  }
  if(authUser?.profileUpdated && authUser?.registrationStatus === "Registered") {
    return (
      <div className="p-6 text-center text-green-600 bg-green-100 rounded-md">
        You have already registered for the conference.
      </div>
    );
  }
  if (formError) {
    toast.error(error.message);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto p-6 bg-white rounded-md"
    >
      <h2 className="text-2xl font-medium mb-4">Update your profile</h2>

      {/* Personal Details */}
      <fieldset className="mb-6">
        <legend className="text-lg font-semibold mb-2">Personal Details</legend>

        <div className="flex flex-col mb-4">
          <label className="mb-1">Prefix</label>
          <select
            name="prefix"
            value={formData.prefix}
            onChange={handleChange}
            className="border p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            required
          >
            <option value="">Select Prefix</option>
            <option value="Ms.">Ms.</option>
            <option value="Mr.">Mr.</option>
            <option value="Dr.">Dr.</option>
            <option value="Prof.">Prof.</option>
          </select>
        </div>

        {/* Name Fields: First Name, Middle Name, Last Name */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="mb-1 block">First Name</label>
            <input
              required
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="border p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block">Middle Name</label>
            <input
              type="text"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              className="border p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block">Last Name</label>
            <input
              required
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="border p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            />
          </div>
        </div>

        <div className="flex flex-col mb-4">
          <label className="mb-1">Email</label>
          <input
            required
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="border p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
          />
        </div>

        <div className="flex flex-col mb-4">
          <label className="mb-1">Designation</label>
          <input
            required
            type="text"
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            className="border p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
          />
        </div>

        <div className="flex flex-col mb-4">
          <label className="mb-1">Institute/University/College</label>
          <input
            required
            type="text"
            name="institute"
            value={formData.institute}
            onChange={handleChange}
            className="border p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
          />
        </div>

        <div className="flex items-center mb-4">
          <span className="mr-4">Gender</span>
          <label className="mr-4">
            <input
              required
              type="radio"
              name="gender"
              value="Male"
              checked={formData.gender === "Male"}
              onChange={handleChange}
            />{" "}
            Male
          </label>
          <label>
            <input
              required
              type="radio"
              name="gender"
              value="Female"
              checked={formData.gender === "Female"}
              onChange={handleChange}
            />{" "}
            Female
          </label>
        </div>
      </fieldset>

      {/* Contact Details */}
      <fieldset className="mb-6">
        <legend className="text-lg font-semibold mb-2">Contact Details</legend>
        {["address", "city", "state", "pinCode", "country", "phoneNumber"].map(
          (field) => (
            <div className="flex flex-col mb-4" key={field}>
              <label className="capitalize mb-1">
                {field.replace(/([A-Z])/g, " $1").trim()}
              </label>
              <input
                required
                type="text"
                name={field}
                value={formData[field]}
                onChange={handleChange}
                className="border p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
              />
            </div>
          )
        )}
      </fieldset>

      <button
        type="submit"
        className="px-6 py-2 bg-[#4f46e5] text-white rounded-lg hover:bg-[#6a63e9]"
      >
        {
          isRegistering ? <Loader sx={() => ({ color: "#ffffff" })} size={24}/>: "Save"
        }
      </button>
    </form>
  );
};

export default ConferenceRegistrationForm;
